/**
 * Dashboard Component — JAD Enterprises branded
 * Includes: results overview, cooldown timer, send calls, client settings, actions
 */

import { useState, useEffect, useRef, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, IS_REHEARSAL } from '../../services/firebase';
import { useResultCounts } from '../../hooks/useResultCounts';
import { useCredits } from '../../hooks/useCredits';
import { useMonthlyUsage } from '../../hooks/useMonthlyUsage';
import { useDailyUsage } from '../../hooks/useDailyUsage';
import { RequestLimitModal } from './RequestLimitModal';
import { useLeads } from '../../hooks/useLeads';
import { useImpersonation, useEffectiveClientId } from '../../hooks/useImpersonation';
import { useAdmin } from '../../hooks/useAdmin';
import { useBuyLeads } from '../../hooks/useBuyLeads';
import { usePaidLeadOrder } from '../../hooks/usePaidLeadOrder';
import { SendRunButton } from './SendRunButton';
import { DeleteLeadsModal } from './DeleteLeadsModal';
import { ExportEmailModal } from './ExportEmailModal';
import { TestCallModal } from './TestCallModal';
import { BuyCredits } from './BuyCredits';
import { platformFeeStatus } from './platformFeeStatus';
import { ScrubNotificationModal } from './ScrubNotificationModal';
import { DeferredVerificationCard } from './DeferredVerificationCard';
import { PhoneBatchStatus } from './PhoneBatchStatus';
import { PhoneBatchCompleteModal } from './PhoneBatchCompleteModal';
import { PlatformFeeNotificationModal } from './PlatformFeeNotificationModal';
import { PortalHeader } from '../Layout/PortalHeader';

interface CooldownTimerProps {
  cooldownUntil: number;
  /**
   * Fires once when the countdown crosses zero so the parent can drop its own
   * `cooldownUntil` and re-enable the run buttons on natural expiry. Without
   * it the card said "Ready" while the buttons stayed disabled until some
   * unrelated snapshot re-rendered the Dashboard.
   */
  onExpired?: () => void;
  /**
   * Admin cool-off override (impersonation only — Dashboard passes it only
   * when `isImpersonating && isAdmin`; the callable re-verifies admin). When
   * set and the timer is active, a button renders under the countdown.
   */
  onOverride?: () => void;
  overriding?: boolean;
  overrideResult?: string | null;
}

function CooldownTimer({
  cooldownUntil, onExpired, onOverride, overriding = false, overrideResult = null,
}: CooldownTimerProps) {
  const [remaining, setRemaining] = useState('');
  const [active, setActive] = useState(false);
  // Latest callback without restarting the 1-second interval on every render.
  // Updated in an effect (not in the render body) so a discarded concurrent
  // render can never leave the ref pointing at an uncommitted callback.
  const onExpiredRef = useRef(onExpired);
  useEffect(() => { onExpiredRef.current = onExpired; });

  useEffect(() => {
    let wasActive = cooldownUntil > Date.now();
    const tick = () => {
      const diff = cooldownUntil - Date.now();
      if (diff <= 0) {
        setRemaining('Ready');
        setActive(false);
        if (wasActive) {
          wasActive = false;
          onExpiredRef.current?.();
        }
      } else {
        const min = Math.floor(diff / 60000);
        const sec = Math.floor((diff % 60000) / 1000);
        setRemaining(`${min}:${String(sec).padStart(2, '0')}`);
        setActive(true);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [cooldownUntil]);

  return (
    <div className="stat-card teal" style={{ textAlign: 'center' }}>
      <h3>Time Before Next Send</h3>
      <span className="count" style={{ color: active ? '#5eead4' : 'var(--green)' }}>
        {remaining}
      </span>
      {!active && <div style={{ fontSize: '0.8rem', color: 'var(--green)', marginTop: '4px' }}>You can send calls now</div>}
      {active && onOverride && (
        <button
          type="button"
          onClick={onOverride}
          disabled={overriding}
          className="btn btn-secondary"
          style={{ marginTop: '10px', fontSize: '0.8rem', padding: '6px 14px', borderColor: 'var(--gold-dark)', color: 'var(--gold)' }}
          title="Admin only: clears this client's cool-off so calls can be sent now. The next send starts a new cool-off as normal."
        >
          {overriding ? 'Clearing…' : 'Admin: override cool-off'}
        </button>
      )}
      {overrideResult && (
        <div style={{ fontSize: '0.75rem', marginTop: '6px', color: overrideResult.startsWith('Error') ? 'var(--red)' : 'var(--green)' }}>
          {overrideResult}
        </div>
      )}
    </div>
  );
}

export function Dashboard() {
  const { isImpersonating, impersonatedClientId, impersonatedClientName, stopImpersonation } = useImpersonation();
  const effectiveClientId = useEffectiveClientId();
  // Server-side count() aggregations — accurate at any size. Replaces the
  // prior limit(10000) JS-side counter which silently undercounted clients
  // past the 10K-result mark (the visible window slid forward and old
  // good_leads rows got evicted by newer bad_leads rows).
  const { counts, loading: resultsLoading } = useResultCounts();
  const { balance, contactBalance, billingType, platformPaidUntil, activeBalance, loading: creditsLoading } = useCredits();
  // Days left on the monthly platform fee (usage card). Follows "Enter as Client"
  // because useCredits() already reads the effective client.
  const feeStatus = platformFeeStatus(platformPaidUntil, Date.now());
  const feeColor = feeStatus.level === 'danger' ? 'var(--red)' : feeStatus.level === 'warn' ? 'var(--gold)' : 'var(--green)';
  const { runCounts, loading: leadsLoading } = useLeads();
  // Blue "Buy New Leads" button — gated by the rollout flag (per-client ?? global default).
  const { enabled: buyLeadsEnabled, startOrder: startBuyLeads, starting: buyLeadsStarting } = useBuyLeads();
  const paidLeadOrder = usePaidLeadOrder();
  const [paidReminderDismissed, setPaidReminderDismissed] = useState(false);

  // Admin cool-off override — only offered while impersonating AND the
  // signed-in user is an admin (defence in depth; the callable is the gate).
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [overridingCooldown, setOverridingCooldown] = useState(false);
  const [cooldownOverrideResult, setCooldownOverrideResult] = useState<string | null>(null);

  // Client settings (editable)
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [recallNoAnswer, setRecallNoAnswer] = useState(5);
  const [recallAnswered, setRecallAnswered] = useState(30);
  const [windowStart, setWindowStart] = useState('09:00');
  const [windowEnd, setWindowEnd] = useState('17:00');
  // Per-client cap from settings.maxCallsPerRun. 2000 default matches new-client
  // creation; admin can raise to 5000 from ManageClient.tsx.
  const [maxCallsPerRun, setMaxCallsPerRun] = useState(2000);
  const [saving, setSaving] = useState(false);
  const [settingsResult, setSettingsResult] = useState<string | null>(null);

  // Inbound calling — user toggle, with credit-deduction confirmation modal.
  const [inboundEnabled, setInboundEnabled] = useState(false);
  const [inboundAgentId, setInboundAgentId] = useState('');
  const [inboundAdminOverride, setInboundAdminOverride] = useState(false);
  const [showInboundConfirm, setShowInboundConfirm] = useState(false);
  const [inboundToggleSaving, setInboundToggleSaving] = useState(false);
  const [inboundResult, setInboundResult] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTestCall, setShowTestCall] = useState(false);
  const [testCallsRemaining, setTestCallsRemaining] = useState(5);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
  const [exportModal, setExportModal] = useState<{ type: string; label: string } | null>(null);

  // Scrub notification (one-time per session — once dismissed, won't show again until a new login)
  const [scrubNotification, setScrubNotification] = useState<{ count: number; date: string } | null>(null);
  const [scrubAlreadyShown, setScrubAlreadyShown] = useState(false);

  // Monthly usage (limit + how much used this calendar month CST)
  const monthlyUsage = useMonthlyUsage(
    effectiveClientId,
    (billingType === 'per_contact' ? 'per_contact' : 'per_call') as 'per_call' | 'per_contact'
  );

  // Daily usage (resets at midnight CST). Tracks all calls dispatched today
  // regardless of billing type, matching the dispatcher's daily cap field.
  const dailyUsage = useDailyUsage(effectiveClientId);

  // Limit-raise request modal (opens when user clicks "Request Higher Limit")
  const [showRequestLimit, setShowRequestLimit] = useState(false);

  // Phone batch completion notification (one-time per session — same pattern as scrub)
  const [batchCompleteNotification, setBatchCompleteNotification] = useState<{
    runType: string;
    goodAdded: number;
    badRemoved: number;
    completedAt: string;
  } | null>(null);
  const [batchNotifAlreadyShown, setBatchNotifAlreadyShown] = useState(false);

  // Platform-fee notification (one-time per session — same pattern as scrub/batch)
  // Triggered by createPhoneBatchJob / addDeferredVerification when client lacks platformPaidUntil.
  const [platformFeeNotification, setPlatformFeeNotification] = useState<{
    source: 'verification' | 'deferred' | 'good_full_only' | 'send_attempt';
  } | null>(null);
  const [platformFeeNotifAlreadyShown, setPlatformFeeNotifAlreadyShown] = useState(false);

  // Handle Stripe redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      setPaymentMessage('Payment successful! Credits will appear shortly.');
      window.history.replaceState({}, '', '/');
      setTimeout(() => setPaymentMessage(null), 8000);
    } else if (payment === 'cancelled') {
      setPaymentMessage('Payment cancelled.');
      window.history.replaceState({}, '', '/');
      setTimeout(() => setPaymentMessage(null), 5000);
    }
  }, []);

  useEffect(() => {
    if (!effectiveClientId) return;
    const unsubscribe = onSnapshot(doc(db, 'clients', effectiveClientId), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setCooldownUntil((data.cooldownUntil as number) || 0);
        const s = data.settings as Record<string, unknown> || {};
        setRecallNoAnswer((s.recallDaysNoAnswer as number) || 5);
        setRecallAnswered((s.recallDaysAnswered as number) || 30);
        setWindowStart((s.callingWindowStart as string) || '09:00');
        setWindowEnd((s.callingWindowEnd as string) || '17:00');
        setMaxCallsPerRun((s.maxCallsPerRun as number) || 2000);

        // Inbound calling state — admin sets the agent ID + force-disable
        // override; user toggles enabled/disabled.
        setInboundEnabled(Boolean(data.inboundEnabled));
        setInboundAgentId(typeof data.inboundAgentId === 'string' ? data.inboundAgentId : '');
        setInboundAdminOverride(Boolean(data.inboundAdminOverride));

        // Test call daily counter
        const tc = data.testCallsToday as { date: string; count: number } | undefined;
        const today = new Date().toISOString().split('T')[0];
        const used = (tc?.date === today) ? (tc.count || 0) : 0;
        setTestCallsRemaining(5 - used);

        // Scrub notification — show once per cleanup event.
        // Clear the server flag immediately on first render so closing the tab
        // (instead of clicking "Got It") doesn't cause it to re-trigger on next login.
        if (!scrubAlreadyShown && data.pendingScrubNotification === true) {
          setScrubNotification({
            count: (data.lastScrubCount as number) || 0,
            date: (data.lastScrubDate as string) || new Date().toISOString().split('T')[0],
          });
          setScrubAlreadyShown(true);
          httpsCallable(functions, 'dismissScrubNotification')({}).catch(err => {
            console.error('Failed to auto-clear scrub notification flag:', err);
          });
        }

        // Phone batch completion notification — show once per batch.
        // Same auto-clear pattern as scrub above.
        const pbn = data.pendingPhoneBatchNotification as Record<string, unknown> | undefined;
        if (!batchNotifAlreadyShown && pbn && typeof pbn === 'object') {
          setBatchCompleteNotification({
            runType: (pbn.runType as string) || 'verification',
            goodAdded: (pbn.goodAdded as number) || 0,
            badRemoved: (pbn.badRemoved as number) || 0,
            completedAt: (pbn.completedAt as string) || '',
          });
          setBatchNotifAlreadyShown(true);
          httpsCallable(functions, 'dismissPhoneBatchNotification')({}).catch(err => {
            console.error('Failed to auto-clear phone batch notification flag:', err);
          });
        }

        // Platform-fee reminder — show once per session when client has
        // uploaded leads but still owes the $500/30-day platform fee.
        // Server-side (createPhoneBatchJob / addDeferredVerification) sets
        // this after checking platformPaidUntil.
        const pfn = data.pendingPlatformFeeNotification as Record<string, unknown> | undefined;
        if (!platformFeeNotifAlreadyShown && pfn && typeof pfn === 'object') {
          const src = (pfn.source as string) || 'verification';
          const validSrc = (src === 'verification' || src === 'deferred' || src === 'good_full_only' || src === 'send_attempt')
            ? src
            : 'verification';
          setPlatformFeeNotification({ source: validSrc as 'verification' | 'deferred' | 'good_full_only' | 'send_attempt' });
          setPlatformFeeNotifAlreadyShown(true);
        }
      }
    });
    return () => unsubscribe();
  }, [effectiveClientId, scrubAlreadyShown, batchNotifAlreadyShown, platformFeeNotifAlreadyShown]);

  // Inbound toggle. On enable, surface the credit-deduction confirmation
  // modal first; on disable, fire the callable immediately. Disabled when
  // admin has force-disabled OR no inbound agent has been assigned yet.
  const handleInboundToggleClick = () => {
    if (inboundAdminOverride) return;
    if (!inboundEnabled) {
      // User wants to enable — show modal first.
      if (!inboundAgentId) {
        setInboundResult('No inbound agent assigned to your account yet. Contact support.');
        return;
      }
      setShowInboundConfirm(true);
    } else {
      // User wants to disable — no confirmation needed (zero downside).
      void doToggleInbound(false);
    }
  };

  const doToggleInbound = async (enable: boolean) => {
    setInboundToggleSaving(true);
    setInboundResult(null);
    try {
      const fn = httpsCallable(functions, 'updateClientInbound');
      await fn({
        enabled: enable,
        ...(isImpersonating ? { clientId: impersonatedClientId } : {}),
      });
      // The onSnapshot listener will reflect the new state, but we also set
      // it locally so the toggle visually flips before the snapshot fires.
      setInboundEnabled(enable);
      setInboundResult(enable ? 'Inbound calling enabled.' : 'Inbound calling disabled.');
      setTimeout(() => setInboundResult(null), 4000);
    } catch (err) {
      setInboundResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setInboundToggleSaving(false);
      setShowInboundConfirm(false);
    }
  };

  // Admin-only: clear this client's cool-off so more leads can be sent now.
  // One-shot — the next manual send writes a fresh cool-off (dispatcher.ts
  // setCooldown). The onSnapshot above sees the field disappear, so the timer
  // flips to "Ready" and the run buttons re-enable through `isCooledDown`.
  // No confirm dialog on purpose: admin-only, clearly labelled, and the
  // previous value is kept in cooldownOverride.previousUntil + adminAuditLog.
  const handleOverrideCooldown = async () => {
    if (!isImpersonating || !impersonatedClientId) return;
    setOverridingCooldown(true);
    setCooldownOverrideResult(null);
    try {
      const fn = httpsCallable(functions, 'adminClearCooldown');
      // expectedUntil = the value this card is showing. If a send finished in
      // between, the server refuses (`cooldown_changed`) and writes nothing;
      // the snapshot then shows the new timer and the button can be clicked
      // again. Local state is reset ONLY on success (the transaction committed
      // the delete, so it is truthful now and the run buttons re-enable
      // without waiting on the listen stream); on every no-op branch the
      // snapshot stays the only writer of local state.
      const res = await fn({ clientId: impersonatedClientId, source: 'dashboard', expectedUntil: cooldownUntil });
      const data = res.data as { changed: boolean; reason?: string; remainingMinutesCleared?: number };
      if (data.changed) {
        setCooldownOverrideResult(`Cool-off cleared (had ${data.remainingMinutesCleared ?? 0} min left). You can send now.`);
        setCooldownUntil(0);
      } else if (data.reason === 'cooldown_changed') {
        setCooldownOverrideResult('Cool-off changed since you looked (a send just finished). Click again to clear the new one.');
      } else {
        setCooldownOverrideResult('No active cool-off to clear.');
      }
      setTimeout(() => setCooldownOverrideResult(null), 6000);
    } catch (err) {
      setCooldownOverrideResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setOverridingCooldown(false);
    }
  };

  const handleSaveSettings = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSettingsResult(null);
    try {
      const fn = httpsCallable(functions, 'updateClientSettings');
      await fn({
        recallDaysNoAnswer: recallNoAnswer, recallDaysAnswered: recallAnswered,
        callingWindowStart: windowStart, callingWindowEnd: windowEnd,
        ...(isImpersonating ? { clientId: impersonatedClientId } : {}),
      });
      setSettingsResult('Settings saved');
    } catch (err) {
      setSettingsResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setSaving(false);
      setTimeout(() => setSettingsResult(null), 3000);
    }
  };

  // Export is handled by <ExportEmailModal> (server-side streaming export +
  // emailed download link). Buttons just open the modal with the chosen type.

  const isLoading = resultsLoading || creditsLoading || leadsLoading;
  if (isLoading) return <div className="loading">Loading dashboard...</div>;

  const isCooledDown = cooldownUntil > Date.now();

  const handleExitImpersonation = () => {
    const clientId = impersonatedClientId;
    stopImpersonation();
    window.location.href = `/admin/client/${clientId}`;
  };

  return (
    <div>
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 1100,
          background: 'linear-gradient(90deg, #b8860b, #ddb154)',
          padding: '10px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontWeight: 700, color: '#000', fontSize: '0.9rem' }}>
              VIEWING AS: {impersonatedClientName}
            </span>
            <span style={{ color: 'rgba(0,0,0,0.6)', fontSize: '0.75rem', fontFamily: 'monospace' }}>
              ID: {impersonatedClientId}
            </span>
            <Link to={`/admin/client/${impersonatedClientId}`}
              style={{ color: '#000', fontSize: '0.75rem', textDecoration: 'underline' }}
              onClick={(e) => { e.preventDefault(); handleExitImpersonation(); }}>
              Admin Manage Page
            </Link>
          </div>
          <button onClick={handleExitImpersonation}
            style={{
              background: '#000', color: '#ddb154', border: 'none', borderRadius: '6px',
              padding: '6px 16px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
            }}>
            Exit Impersonation
          </button>
        </div>
      )}

      <PortalHeader
        showCredits={true}
        activeBalance={activeBalance}
        billingType={billingType === 'per_contact' ? 'per_contact' : 'per_call'}
        onBuyCreditsClick={() => setShowBuyCredits(true)}
        onBuyLeadsClick={buyLeadsEnabled ? startBuyLeads : undefined}
        buyLeadsBusy={buyLeadsStarting}
      />

      <div className="container">
        {/* Paid-but-unconfigured lead order: persistent banner + once-per-visit popup */}
        {paidLeadOrder && (
          <div style={{
            padding: '12px 16px', borderRadius: '8px', marginBottom: '20px',
            background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap',
          }}>
            <span style={{ color: 'var(--white)', fontSize: '0.9rem' }}>
              Your lead purchase ({paidLeadOrder.itemsSummary}) is paid — pick your filters to receive your leads.
            </span>
            <Link to={`/buy-leads/configure?o=${paidLeadOrder.orderId}`} className="btn btn-blue"
              style={{ padding: '8px 18px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              Finish My Lead Order
            </Link>
          </div>
        )}
        {paidLeadOrder && !paidReminderDismissed && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
          }}>
            <div className="buyleads-card" style={{ maxWidth: '440px', width: '100%' }}>
              <h3 style={{ margin: '0 0 10px', color: 'var(--gold)' }}>Your Leads Are Waiting</h3>
              <p style={{ color: 'var(--gray-300)', fontSize: '0.9rem', marginBottom: '16px' }}>
                Your payment for {paidLeadOrder.itemsSummary} is confirmed. One last step — choose your
                filters so we can generate your leads and load them into Run 1.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setPaidReminderDismissed(true)}>
                  Later
                </button>
                <Link to={`/buy-leads/configure?o=${paidLeadOrder.orderId}`} className="btn btn-blue">
                  Pick My Filters
                </Link>
              </div>
            </div>
          </div>
        )}
        {paymentMessage && (
          <div style={{
            padding: '12px 16px', borderRadius: '8px', marginBottom: '20px',
            background: paymentMessage.includes('successful') ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${paymentMessage.includes('successful') ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: paymentMessage.includes('successful') ? '#10b981' : '#dc2626',
            fontSize: '0.9rem',
          }}>{paymentMessage}</div>
        )}
        {/* While an ADMIN is in "Enter as Client" this is a read-only price preview:
            checkout always bills the CALLER's account, so buying is blocked there.
            Blocked while admin status is still loading (fail closed); a confirmed
            non-admin carrying a stale impersonation flag is never blocked. */}
        <BuyCredits isOpen={showBuyCredits} onClose={() => setShowBuyCredits(false)} billingType={billingType} platformPaidUntil={platformPaidUntil}
          isImpersonating={isImpersonating && (adminLoading || isAdmin)}
          impersonatedClientId={isImpersonating && (adminLoading || isAdmin) ? impersonatedClientId : null} />
        {/* Platform + Billing Status */}
        {billingType === 'per_contact' && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
            background: 'var(--dark-2)', border: '1px solid var(--dark-3)', fontSize: '0.8rem', color: 'var(--gray-400)',
          }}>
            Per Contact billing — credits deducted for calls 30+ seconds where prospect was reached.
            {contactBalance < 0 && (
              <span style={{ color: '#dc2626', fontWeight: 600 }}> Overage: {Math.abs(contactBalance)} credits will be charged.</span>
            )}
            {contactBalance > 0 && (
              <span> Estimated sends: ~{Math.floor(contactBalance / 0.06).toLocaleString()} calls.</span>
            )}
          </div>
        )}
        {activeBalance < 0 && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px',
            background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', color: '#dc2626', fontSize: '0.85rem',
          }}>
            Credit balance is negative ({activeBalance}). Please purchase credits to cover the overage.
          </div>
        )}

        {/* Results Overview */}
        <h2 className="section-title">Results Overview</h2>
        <div className="stats-grid">
          <Link to="/results/good_leads" className="stat-card green">
            <h3>Good Leads</h3>
            <span className="count">{counts.good}</span>
          </Link>
          <Link to="/results/good_leads_later" className="stat-card blue">
            <h3>Good For Later</h3>
            <span className="count">{counts.later}</span>
          </Link>
          <Link to="/results/not_interested" className="stat-card orange">
            <h3>Not Interested</h3>
            <span className="count">{counts.notInterested}</span>
          </Link>
          <Link to="/results/bad_leads" className="stat-card red">
            <h3>Bad Leads</h3>
            <span className="count">{counts.bad}</span>
          </Link>
          {counts.nonAccredited > 0 && (
            <Link to="/results/non_accredited" className="stat-card orange">
              <h3>Non Accredited</h3>
              <span className="count">{counts.nonAccredited}</span>
            </Link>
          )}
        </div>

        {/* Active phone batch verification pill (in-process indicator) */}
        <div style={{ marginBottom: '12px' }}>
          <PhoneBatchStatus clientId={effectiveClientId} />
        </div>

        {/* Deferred Verification Queue Card (only renders when count > 0) */}
        <DeferredVerificationCard
          clientId={effectiveClientId}
          billingType={(billingType === 'per_contact' ? 'per_contact' : 'per_call') as 'per_call' | 'per_contact'}
        />

        {/* Monthly + Daily Usage Card */}
        <div style={{
          padding: '14px 18px', marginBottom: '12px', borderRadius: '10px',
          background: 'var(--dark-2)',
          border: `1px solid ${
            monthlyUsage.percent >= 100 ? 'rgba(239,68,68,0.5)' :
            monthlyUsage.percent >= 80 ? 'rgba(200,168,78,0.5)' :
            'var(--dark-3)'
          }`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '24px',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px', flex: '1 1 auto' }}>
            <div>
              <div style={{ color: 'var(--gray-400)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Monthly Usage &mdash; {monthlyUsage.monthKey}
              </div>
              <div style={{ marginTop: '4px', fontSize: '1.1rem', color: 'var(--white)' }}>
                <strong style={{
                  color: monthlyUsage.percent >= 100 ? 'var(--red)' :
                         monthlyUsage.percent >= 80 ? 'var(--gold)' : 'var(--green)',
                }}>
                  {monthlyUsage.used.toLocaleString()}
                </strong>
                <span style={{ color: 'var(--gray-400)' }}> / {monthlyUsage.limit.toLocaleString()} </span>
                <span style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>
                  {billingType === 'per_contact' ? 'contacts' : 'calls'}
                </span>
              </div>
              <div style={{ marginTop: '6px', height: '6px', width: '240px', background: 'var(--dark-3)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(100, monthlyUsage.percent)}%`,
                  height: '100%',
                  background: monthlyUsage.percent >= 100 ? 'var(--red)' :
                              monthlyUsage.percent >= 80 ? 'var(--gold)' :
                              'var(--green)',
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>

            <div>
              <div style={{ color: 'var(--gray-400)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Today &mdash; {dailyUsage.dayKey} CST
              </div>
              <div style={{ marginTop: '4px', fontSize: '1.1rem', color: 'var(--white)' }}>
                <strong style={{
                  color: dailyUsage.percent >= 100 ? 'var(--red)' :
                         dailyUsage.percent >= 80 ? 'var(--gold)' : 'var(--green)',
                }}>
                  {dailyUsage.sent.toLocaleString()}
                </strong>
                <span style={{ color: 'var(--gray-400)' }}> / {dailyUsage.limit.toLocaleString()} </span>
                <span style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>calls</span>
              </div>
              <div style={{ marginTop: '6px', height: '6px', width: '240px', background: 'var(--dark-3)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${Math.min(100, dailyUsage.percent)}%`,
                  height: '100%',
                  background: dailyUsage.percent >= 100 ? 'var(--red)' :
                              dailyUsage.percent >= 80 ? 'var(--gold)' :
                              'var(--green)',
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>

            {/* Platform fee — days left. Same format as the two blocks beside it.
                Green, gold at 7 days, red at 3 days / expired / never paid. The bar is
                the share of one 30-day period left (paid days stack, so it can sit full). */}
            <div>
              <div style={{ color: 'var(--gray-400)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Platform Fee
                {!creditsLoading && feeStatus.dateKey && (
                  <> &mdash; {feeStatus.state === 'active' ? 'paid through' : 'ended'} {feeStatus.dateKey} CST</>
                )}
              </div>
              <div style={{ marginTop: '4px', fontSize: '1.1rem', color: 'var(--white)' }}>
                {creditsLoading ? (
                  <span style={{ color: 'var(--gray-400)' }}>&mdash;</span>
                ) : feeStatus.state === 'active' ? (
                  <>
                    <strong style={{ color: feeColor }}>{feeStatus.daysLeft.toLocaleString()}</strong>
                    <span style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}> {feeStatus.daysLeft === 1 ? 'day left' : 'days left'}</span>
                  </>
                ) : (
                  <>
                    <strong style={{ color: feeColor }}>{feeStatus.state === 'expired' ? 'Expired' : 'Not active'}</strong>
                    <span style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}> required to send calls</span>
                  </>
                )}
              </div>
              <div style={{ marginTop: '6px', height: '6px', width: '240px', background: 'var(--dark-3)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${creditsLoading ? 0 : feeStatus.percent}%`,
                  height: '100%',
                  background: feeColor,
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {monthlyUsage.percent >= 80 && (
              <button
                onClick={() => setShowRequestLimit(true)}
                className="btn btn-primary"
                style={{ padding: '6px 14px', fontSize: '0.85rem' }}
              >
                Request Higher Limit
              </button>
            )}
          </div>
        </div>

        {/* Total Calls + Export Buttons */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px',
          padding: '12px 16px', borderRadius: '8px', marginBottom: '8px',
          background: 'var(--dark-2)', border: '1px solid var(--dark-3)',
        }}>
          <div style={{ color: 'var(--gray-300)', fontSize: '0.9rem' }}>
            Total Calls Made: <span style={{ color: 'var(--white)', fontWeight: 600 }}>{counts.total.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setExportModal({ type: 'good_all', label: 'Good Leads' })}
              className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '0.8rem' }}
            >
              Export Good Leads
            </button>
            <button
              onClick={() => setExportModal({ type: 'all', label: 'All Calls' })}
              className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '0.8rem' }}
            >
              Export All Calls
            </button>
          </div>
        </div>

        {/* Cooldown Timer — centered above Send Calls */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
          <div style={{ width: '100%', maxWidth: '300px' }}>
            <CooldownTimer
              cooldownUntil={cooldownUntil}
              onExpired={() => setCooldownUntil(0)}
              onOverride={isImpersonating && isAdmin ? handleOverrideCooldown : undefined}
              overriding={overridingCooldown}
              overrideResult={cooldownOverrideResult}
            />
          </div>
        </div>

        {/* Send Calls */}
        <h2 className="section-title">Send Calls</h2>
        <p style={{ color: 'var(--gray-500)', fontSize: '0.8rem', marginBottom: '12px' }}>
          Min 100 / Max {maxCallsPerRun.toLocaleString()} per run (less allowed when available credits or leads are below 100) | Daily limit: 10,000 | {billingType === 'per_contact' ? '10 min cooldown per 200 sent (results must process)' : '15 min cooldown per 200 sent'}
        </p>
        <div className="run-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(run => (
            <SendRunButton
              key={run}
              runNumber={run}
              // `available` reflects what the dispatcher actually picks up:
              // status='pending' AND nextEligibleCallAt is null OR <= now.
              // Recall-waiting leads surface separately in `waiting` so the
              // user understands why a Run shows fewer ready than total.
              available={runCounts[run]?.eligibleNow || 0}
              waiting={runCounts[run]?.waiting || 0}
              earliestEligibleMs={runCounts[run]?.earliestEligibleMs ?? null}
              // IS_REHEARSAL: sending a run dials real phones through Retell — never from the bench.
              disabled={IS_REHEARSAL || (billingType === 'per_call' && balance === 0) || (billingType === 'per_contact' && contactBalance <= -10) || isCooledDown}
              clientId={isImpersonating ? impersonatedClientId : null}
              balance={balance}
              billingType={(billingType === 'per_contact' ? 'per_contact' : 'per_call')}
              maxPerRun={maxCallsPerRun}
              onBuyPlatform={() => setShowBuyCredits(true)}
            />
          ))}
        </div>

        {/* Upload + Delete Leads + Test Call */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px', marginBottom: '24px' }}>
          {/* Hidden on the rehearsal bench: a test call dials through Retell, and
              the Leads page calls a live phone-validation URL. */}
          {!IS_REHEARSAL && <button onClick={() => setShowTestCall(true)} className="btn btn-secondary"
            style={{ fontSize: '0.8rem', padding: '6px 16px', borderColor: 'var(--gold-dark)', color: 'var(--gold)' }}>
            Test My Agent {testCallsRemaining < 5 ? `(${testCallsRemaining})` : ''}
          </button>}
          {!IS_REHEARSAL && <Link to="/leads" className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '6px 16px' }}>
            Upload Leads
          </Link>}
          <Link to="/schedule-calls" className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '6px 16px' }}>
            Schedule Calls
          </Link>
          <button onClick={() => setShowDeleteModal(true)} className="btn btn-danger" style={{ fontSize: '0.8rem', padding: '6px 16px' }}>
            Delete Leads
          </button>
        </div>
        <TestCallModal isOpen={showTestCall} onClose={() => setShowTestCall(false)} remaining={testCallsRemaining} clientId={isImpersonating ? impersonatedClientId : null} />
        <DeleteLeadsModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} runCounts={runCounts} clientId={isImpersonating ? impersonatedClientId : null} />
        <ExportEmailModal
          isOpen={!!exportModal}
          onClose={() => setExportModal(null)}
          exportType={exportModal?.type || 'all'}
          exportLabel={exportModal?.label || ''}
          clientId={isImpersonating ? impersonatedClientId : undefined}
        />
        {scrubNotification && (
          <ScrubNotificationModal
            removedCount={scrubNotification.count}
            scrubDate={scrubNotification.date}
            onClose={() => setScrubNotification(null)}
          />
        )}
        {showRequestLimit && (
          <RequestLimitModal
            defaultName={(isImpersonating ? impersonatedClientName : '') || ''}
            defaultEmail={''}
            onClose={() => setShowRequestLimit(false)}
          />
        )}
        {batchCompleteNotification && (
          <PhoneBatchCompleteModal
            runType={batchCompleteNotification.runType}
            goodAdded={batchCompleteNotification.goodAdded}
            badRemoved={batchCompleteNotification.badRemoved}
            completedAt={batchCompleteNotification.completedAt}
            onClose={() => setBatchCompleteNotification(null)}
          />
        )}

        {platformFeeNotification && (
          <PlatformFeeNotificationModal
            source={platformFeeNotification.source}
            onClose={() => setPlatformFeeNotification(null)}
            onBuyPlatform={() => {
              setPlatformFeeNotification(null);
              setShowBuyCredits(true);
            }}
          />
        )}

        {/* Client Settings */}
        <h2 className="section-title">Call Settings</h2>
        <form onSubmit={handleSaveSettings} style={{ marginBottom: '32px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div className="form-group">
              <label>Recall Days (No Answer)</label>
              <input type="number" value={recallNoAnswer} onChange={e => setRecallNoAnswer(parseInt(e.target.value) || 5)} min="1" max="90" />
            </div>
            <div className="form-group">
              <label>Recall Days (Answered)</label>
              <input type="number" value={recallAnswered} onChange={e => setRecallAnswered(parseInt(e.target.value) || 30)} min="1" max="180" />
            </div>
            <div className="form-group">
              <label>Window Start (CST)</label>
              <input type="time" value={windowStart} onChange={e => setWindowStart(e.target.value)} min="08:00" max="18:00" />
            </div>
            <div className="form-group">
              <label>Window End (CST)</label>
              <input type="time" value={windowEnd} onChange={e => setWindowEnd(e.target.value)} min="09:00" max="19:00" />
            </div>
          </div>
          <p style={{ color: 'var(--gray-500)', fontSize: '0.75rem', marginBottom: '8px' }}>
            Calling window enforced 8:00 AM - 7:00 PM CST (Central Standard Time)
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ padding: '8px 20px' }}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            {settingsResult && (
              <span style={{ fontSize: '0.85rem', color: settingsResult.startsWith('Error') ? 'var(--red)' : 'var(--green)' }}>
                {settingsResult}
              </span>
            )}
          </div>
        </form>

        {/* Inbound Calls toggle — shown only when admin has assigned an
            inbound agent OR has force-disabled (so user knows it's blocked). */}
        {(inboundAgentId || inboundAdminOverride) && (
          <section style={{ marginBottom: '32px', padding: '16px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0 }}>Receive Inbound Calls</h3>
            <p style={{ opacity: 0.7, marginTop: '4px', marginBottom: '12px', fontSize: '0.9rem' }}>
              When enabled, leads who call back the number we dialled them on will be
              answered by your inbound agent.{' '}
              {billingType === 'per_contact'
                ? <strong>Each qualifying callback (≥20 seconds, within 72 hours of your outbound call) deducts 1 contact credit.</strong>
                : <strong>Callbacks are free on your per-call plan.</strong>}
            </p>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: inboundAdminOverride || inboundToggleSaving ? 'not-allowed' : 'pointer',
              opacity: inboundAdminOverride ? 0.5 : 1,
            }}>
              <input
                type="checkbox"
                checked={inboundEnabled}
                onChange={handleInboundToggleClick}
                disabled={inboundAdminOverride || inboundToggleSaving}
              />
              {inboundToggleSaving ? 'Saving…' : 'Inbound Calls Enabled'}
            </label>
            {inboundAdminOverride && (
              <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '0.85rem', color: 'var(--red)' }}>
                Disabled by administrator. Contact support to re-enable.
              </p>
            )}
            {inboundResult && (
              <p style={{
                marginTop: '8px', marginBottom: 0, fontSize: '0.85rem',
                color: inboundResult.startsWith('Error') ? 'var(--red)' : 'var(--green)',
              }}>{inboundResult}</p>
            )}
          </section>
        )}

        {/* Inbound credit-confirmation modal — shown only when user is enabling. */}
        {showInboundConfirm && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000,
          }}>
            <div style={{
              background: 'var(--dark-2)', border: '1px solid var(--dark-3)',
              borderRadius: '12px', padding: '28px', maxWidth: '480px', width: '90%',
            }}>
              <h3 style={{ marginTop: 0, color: 'var(--gold)' }}>Confirm Inbound Calling</h3>
              <p style={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
                Enabling inbound calls will route callbacks from your leads to your inbound
                agent.{' '}
                {billingType === 'per_contact'
                  ? <>Each qualifying callback will deduct <strong>1 contact credit</strong> from your balance.</>
                  : <>Callbacks are <strong>free</strong> on your per-call plan.</>}
              </p>
              {billingType === 'per_contact' ? (
                <p style={{ fontSize: '0.9rem', lineHeight: 1.5, color: 'var(--gray-300)' }}>
                  <strong>Qualifying call:</strong> ≥20 seconds, callback to a number you previously
                  dialled, made within 72 hours of that outbound call. One credit per callback per
                  run. You can disable this any time.
                </p>
              ) : (
                <p style={{ fontSize: '0.9rem', lineHeight: 1.5, color: 'var(--gray-300)' }}>
                  You can disable this any time.
                </p>
              )}
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button
                  onClick={() => doToggleInbound(true)}
                  className="btn btn-primary"
                  disabled={inboundToggleSaving}
                  style={{ flex: 1 }}
                >
                  {inboundToggleSaving ? 'Enabling…' : 'I Understand — Enable Inbound'}
                </button>
                <button
                  onClick={() => setShowInboundConfirm(false)}
                  className="btn btn-secondary"
                  disabled={inboundToggleSaving}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
